import { AddIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Spacer,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { Facility, MetriportMedicalApi } from "@metriport/api-sdk";
import { useContext, useState } from "react";
import { BsPeopleFill } from "react-icons/bs";
import { FaClinicMedical } from "react-icons/fa";
import { capture } from "../../../shared/capture";
import { MedicalAppStateContext } from "../../contexts/medical";
import { MedicalAppStateActionType, PatientsMap } from "../../contexts/medical/reducer";
import { Actions, Analytics, Features } from "../../shared/analytics";
import { Button } from "../../shared/Button";
import useMetriportToast from "../../shared/toast";
import FacilityForm from "./form";

export default function Facilities({ api }: { api: MetriportMedicalApi }) {
  const bgColor = useColorModeValue("white", "gray.700");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [gettingPatients, setGettingPatients] = useState<boolean>(false);
  const [facilityToEdit, setFacilityToEdit] = useState<string | undefined>(undefined);
  const { medicalState, medicalDispatch } = useContext(MedicalAppStateContext);
  const toast = useMetriportToast();

  async function onGetPatients(facility: Facility): Promise<void> {
    Analytics.emit(Actions.get, Features.patient);
    try {
      setGettingPatients(true);
      const patients = await api.listPatients(facility.id);
      const patientsMap: PatientsMap = {};
      for (const patient of patients) {
        patientsMap[patient.id] = patient;
      }
      medicalDispatch({
        type: MedicalAppStateActionType.update,
        newState: {
          store: { ...medicalState.store, patients: patientsMap },
          selectedFacility: facility,
        },
      });
    } catch (err) {
      capture.error(err, {
        extra: { facilityId: facility.id, context: `facility.getPatients` },
      });
      toast.error();
    }
    setGettingPatients(false);
  }

  function onAddFacility(): void {
    Analytics.emit(Actions.open, Features.form, { type: Features.facility });
    onOpen();
  }

  function onEditFacility(facilityId: string): void {
    Analytics.emit(Actions.open, Features.form, { type: Features.facility });
    setFacilityToEdit(facilityId);
    onOpen();
  }

  const closeForm = () => {
    Analytics.emit(Actions.close, Features.form, { type: Features.facility });
    setFacilityToEdit(undefined);
    onClose();
  };

  return (
    <Stack spacing={4} w={"max-content"} bg={bgColor} rounded={"xl"} boxShadow={"lg"} p={6} my={12}>
      <Flex minWidth={"max-content"} alignItems="center" gap={"2"}>
        <HStack>
          <Icon as={FaClinicMedical} />
          <Heading as="h4" size="md">
            Facilities
          </Heading>
        </HStack>
        <Spacer />
        <Button onClick={onAddFacility} leftIcon={<AddIcon />}>
          Add
        </Button>
      </Flex>
      <Box>
        <TableContainer>
          <Table variant="striped">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Name</Th>
                <Th>State</Th>
              </Tr>
            </Thead>
            <Tbody>
              {medicalState.store?.facilities ? (
                Object.keys(medicalState.store?.facilities).map((id, i) => {
                  // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                  const facility = medicalState.store?.facilities![id];
                  if (!facility) return;
                  return (
                    <Tr key={i}>
                      <Td>{facility.id}</Td>
                      <Td>{facility.name}</Td>
                      <Td>{facility.address.state}</Td>
                      <Td>
                        <Button onClick={() => onEditFacility(facility.id)} leftIcon={<EditIcon />}>
                          Edit
                        </Button>
                      </Td>
                      <Td>
                        <Button
                          isLoading={gettingPatients}
                          onClick={() => onGetPatients(facility)}
                          leftIcon={<BsPeopleFill />}
                        >
                          Patients
                        </Button>
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <></>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
      <FacilityForm facilityId={facilityToEdit} isOpen={isOpen} api={api} onClose={closeForm} />
    </Stack>
  );
}
